import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Confetti } from "@/components/tools/Confetti";
import {
  CopyButton,
  BrandedExportActions,
  EmptyHint,
  FullscreenButton,
  HistoryPanel,
  ListInput,
  Panel,
  ResetButton,
  SoundToggle,
  usePersistentState,
  useSfx,
} from "@/components/tools/ToolKit";
import { COLORS, type PresetBank } from "@/lib/presets";
import {
  DEFAULT_CLASS_LIST,
  dedupe,
  parseList,
  pickOne,
  randomInt,
  shuffle,
} from "@/lib/random-engine";

type Phase = "IDLE" | "RUNNING" | "RESULT";

/* ---------------- Generic preset card picker ---------------- */
export function CardPicker({
  storageKey,
  presets,
  actionLabel = "Pick",
  entriesLabel = "Items",
  emptyMessage = "Your list is empty. Add some items first.",
  celebrate = false,
}: {
  storageKey: string;
  presets: PresetBank[];
  actionLabel?: string;
  entriesLabel?: string;
  emptyMessage?: string;
  celebrate?: boolean;
}) {
  const [bank, setBank] = usePersistentState(`${storageKey}:bank`, presets[0]?.name ?? "Custom");
  const [custom, setCustom] = usePersistentState(`${storageKey}:custom`, "");
  const [soundOn, setSoundOn] = usePersistentState(`${storageKey}:sound`, true);
  const [noRepeat, setNoRepeat] = useState(true);
  const [phase, setPhase] = useState<Phase>("IDLE");
  const [display, setDisplay] = useState("Ready when you are");
  const [result, setResult] = useState<string | null>(null);
  const [used, setUsed] = useState<string[]>([]);
  const [history, setHistory] = useState<string[]>([]);
  const [confetti, setConfetti] = useState(false);
  const stageRef = useRef<HTMLDivElement>(null);
  const sfx = useSfx(soundOn);

  const isCustom = bank === "Custom";
  const items = dedupe(
    isCustom ? parseList(custom) : (presets.find((p) => p.name === bank)?.items ?? []),
  );
  const pool = noRepeat ? items.filter((i) => !used.includes(i)) : items;
  const canStart = items.length >= 1 && phase !== "RUNNING";

  const start = () => {
    if (!canStart) return;
    const source = pool.length ? pool : items;
    if (!pool.length) setUsed([]);
    const chosen = pickOne(source);
    if (!chosen) return;
    setPhase("RUNNING");
    setResult(null);
    setConfetti(false);
    sfx.whoosh();

    const total = 900;
    const startedAt = performance.now();
    const step = () => {
      const elapsed = performance.now() - startedAt;
      if (elapsed >= total) {
        setDisplay(chosen);
        setResult(chosen);
        setPhase("RESULT");
        setUsed((u) => [...u, chosen]);
        setHistory((h) => [chosen, ...h].slice(0, 50));
        sfx.fanfare();
        if (celebrate) {
          setConfetti(true);
          window.setTimeout(() => setConfetti(false), 2200);
        }
        return;
      }
      setDisplay(items[randomInt(items.length)] ?? chosen);
      sfx.tick();
      window.setTimeout(step, 60 + (elapsed / total) * 120);
    };
    step();
  };

  return (
    <>
      <div className="grid gap-6 lg:grid-cols-[340px_1fr_320px] xl:grid-cols-[360px_1fr_340px]">
        {/* Settings Panel */}
        <Panel title="Categories & Items" className="no-print space-y-4">
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Bank Category
            </p>
            <div className="flex flex-wrap gap-1.5">
              {[...presets.map((p) => p.name), "Custom"].map((name) => (
                <Button
                  key={name}
                  type="button"
                  size="sm"
                  variant={bank === name ? "default" : "outline"}
                  className="font-semibold text-xs"
                  onClick={() => {
                    setBank(name);
                    setUsed([]);
                    sfx.click();
                  }}
                >
                  {name}
                </Button>
              ))}
            </div>
          </div>

          {isCustom ? (
            <ListInput
              value={custom}
              onChange={setCustom}
              label={entriesLabel}
              count={items.length}
            />
          ) : (
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  Available Words ({items.length})
                </span>
                {noRepeat && (
                  <span className="text-xs text-primary font-bold">
                    {used.length}/{items.length} Used
                  </span>
                )}
              </div>
              <ul className="max-h-56 space-y-1 overflow-auto rounded-xl border bg-secondary/30 p-3 text-sm">
                {items.map((i) => {
                  const isUsed = noRepeat && used.includes(i);
                  return (
                    <li
                      key={i}
                      className={`truncate px-2 py-1 rounded-md text-xs font-medium transition-colors ${
                        isUsed ? "line-through opacity-40 bg-secondary/50" : "bg-card border border-border/50"
                      }`}
                    >
                      {i}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          <div className="flex items-center justify-between rounded-xl border bg-secondary/50 px-3.5 py-2.5">
            <span className="text-xs font-medium">No repeats until all used</span>
            <Switch checked={noRepeat} onCheckedChange={setNoRepeat} />
          </div>
        </Panel>

        {/* Main Stage Display */}
        <div
          ref={stageRef}
          className="surface-card flex flex-col items-center justify-between gap-6 p-6 min-h-[460px] sm:min-h-[540px] lg:min-h-[600px] xl:min-h-[660px]"
        >
          <Confetti active={confetti} />
          <div className="w-full flex items-center justify-between no-print">
            <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground bg-secondary px-3 py-1 rounded-full">
              Category: {bank}
            </span>
            <FullscreenButton targetRef={stageRef} />
          </div>

          {/* Prompt Display Card */}
          <div className="my-auto flex flex-col items-center justify-center text-center p-6 sm:p-8 w-full max-w-2xl rounded-2xl border border-primary/20 bg-primary/5 min-h-[220px]">
            <p
              aria-live="polite"
              className={`font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight break-words ${
                phase === "RESULT" ? "animate-reveal text-primary" : "text-foreground opacity-80"
              }`}
            >
              {display}
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col items-center gap-3 w-full">
            {items.length === 0 ? (
              <EmptyHint>{emptyMessage}</EmptyHint>
            ) : (
              <Button
                size="lg"
                className="h-14 px-12 text-lg font-bold"
                disabled={phase === "RUNNING"}
                onClick={start}
              >
                {phase === "RESULT" ? `${actionLabel} Again` : actionLabel}
              </Button>
            )}

            <div className="no-print flex flex-wrap justify-center gap-2">
              <SoundToggle on={soundOn} onToggle={() => setSoundOn(!soundOn)} />
              {result ? (
                <>
                  <CopyButton text={result} label="Copy Result" />
                  <BrandedExportActions
                    title={actionLabel}
                    content={result}
                    filename="picamini-picker-result.png"
                  />
                </>
              ) : null}
              <ResetButton
                onClick={() => {
                  setPhase("IDLE");
                  setDisplay("Ready when you are");
                  setResult(null);
                  setUsed([]);
                  setHistory([]);
                }}
              />
            </div>
          </div>
        </div>

        {/* History Panel */}
        <HistoryPanel items={history} onClear={() => setHistory([])} title="Recent Picks" />
      </div>
    </>
  );
}

/* ---------------- Random Letter Picker ---------------- */
const ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");
const VOWELS = ["A", "E", "I", "O", "U"];

export function LetterPicker() {
  const [soundOn, setSoundOn] = usePersistentState("pickamini:letter:sound", true);
  const [mode, setMode] = useState<"all" | "vowels" | "consonants">("all");
  const [running, setRunning] = useState(false);
  const [letter, setLetter] = useState("?");
  const [history, setHistory] = useState<string[]>([]);
  const stageRef = useRef<HTMLDivElement>(null);
  const sfx = useSfx(soundOn);

  const pool =
    mode === "vowels"
      ? VOWELS
      : mode === "consonants"
        ? ALPHABET.filter((l) => !VOWELS.includes(l))
        : ALPHABET;

  const start = () => {
    if (running) return;
    const chosen = pool[randomInt(pool.length)]!;
    setRunning(true);
    sfx.whoosh();
    let n = 0;
    const step = () => {
      if (n++ > 14) {
        setLetter(chosen);
        setRunning(false);
        setHistory((h) => [chosen, ...h].slice(0, 50));
        sfx.fanfare();
        return;
      }
      setLetter(pool[randomInt(pool.length)]!);
      sfx.tick();
      window.setTimeout(step, 50 + n * 8);
    };
    step();
  };

  return (
    <div className="grid gap-5 lg:grid-cols-[280px_1fr_280px]">
      <Panel title="Letters" className="no-print">
        <div className="flex flex-wrap gap-2">
          {(["all", "vowels", "consonants"] as const).map((m) => (
            <Button
              key={m}
              size="sm"
              variant={mode === m ? "default" : "outline"}
              onClick={() => setMode(m)}
            >
              {m === "all" ? "A–Z" : m === "vowels" ? "Vowels" : "Consonants"}
            </Button>
          ))}
        </div>
        <p className="mt-3 text-sm text-muted-foreground">{pool.length} letters in the pool.</p>
      </Panel>
      <div
        ref={stageRef}
        className="surface-card flex flex-col items-center justify-center gap-6 bg-card p-10"
      >
        <span
          aria-live="polite"
          className={`font-display text-[7rem] font-bold leading-none text-primary ${running ? "animate-tick-flash" : "animate-reveal"}`}
        >
          {letter}
        </span>
        <Button
          size="lg"
          className="h-14 px-12 text-lg font-semibold shadow-lift"
          disabled={running}
          onClick={start}
        >
          {running ? "Picking…" : "Pick a letter"}
        </Button>
        <div className="no-print flex flex-wrap justify-center gap-2">
          <SoundToggle on={soundOn} onToggle={() => setSoundOn(!soundOn)} />
          <FullscreenButton targetRef={stageRef} />
          <ResetButton
            onClick={() => {
              setLetter("?");
              setHistory([]);
            }}
          />
        </div>
      </div>
      <HistoryPanel items={history} onClear={() => setHistory([])} />
    </div>
  );
}

/* ---------------- Yes or No Picker ---------------- */
export function YesNoPicker() {
  const [soundOn, setSoundOn] = usePersistentState("pickamini:yesno:sound", true);
  const [includeMaybe, setIncludeMaybe] = useState(false);
  const [running, setRunning] = useState(false);
  const [answer, setAnswer] = useState<string | null>(null);
  const [history, setHistory] = useState<string[]>([]);
  const stageRef = useRef<HTMLDivElement>(null);
  const sfx = useSfx(soundOn);

  const start = () => {
    if (running) return;
    const options = includeMaybe ? ["Yes", "No", "Maybe"] : ["Yes", "No"];
    const chosen = options[randomInt(options.length)]!;
    setRunning(true);
    setAnswer(null);
    sfx.whoosh();
    let n = 0;
    const step = () => {
      if (n++ > 12) {
        setAnswer(chosen);
        setRunning(false);
        setHistory((h) => [chosen, ...h].slice(0, 50));
        sfx.fanfare();
        return;
      }
      setAnswer(options[n % options.length]!);
      sfx.tick();
      window.setTimeout(step, 55 + n * 10);
    };
    step();
  };

  const tone =
    answer === "Yes"
      ? "text-[oklch(0.6_0.15_150)]"
      : answer === "No"
        ? "text-destructive"
        : "text-accent-foreground";

  return (
    <div className="grid gap-5 lg:grid-cols-[280px_1fr_280px]">
      <Panel title="Options" className="no-print">
        <div className="flex items-center justify-between rounded-lg bg-secondary/70 px-3 py-2">
          <span className="text-sm font-medium">Include “Maybe”</span>
          <Switch checked={includeMaybe} onCheckedChange={setIncludeMaybe} />
        </div>
      </Panel>
      <div
        ref={stageRef}
        className="surface-card flex flex-col items-center justify-center gap-6 bg-card p-10"
      >
        <span
          aria-live="polite"
          className={`font-display text-6xl font-bold ${running ? "animate-tick-flash" : `animate-reveal ${tone}`}`}
        >
          {answer ?? "Ask a question"}
        </span>
        <Button
          size="lg"
          className="h-14 px-12 text-lg font-semibold shadow-lift"
          disabled={running}
          onClick={start}
        >
          {running ? "Deciding…" : "Decide"}
        </Button>
        <div className="no-print flex flex-wrap justify-center gap-2">
          <SoundToggle on={soundOn} onToggle={() => setSoundOn(!soundOn)} />
          <FullscreenButton targetRef={stageRef} />
          <ResetButton
            onClick={() => {
              setAnswer(null);
              setHistory([]);
            }}
          />
        </div>
      </div>
      <HistoryPanel items={history} onClear={() => setHistory([])} />
    </div>
  );
}

/* ---------------- Random Color Picker ---------------- */
export function ColorPicker() {
  const [soundOn, setSoundOn] = usePersistentState("pickamini:color:sound", true);
  const [running, setRunning] = useState(false);
  const [color, setColor] = useState(COLORS[6]!);
  const [history, setHistory] = useState<string[]>([]);
  const stageRef = useRef<HTMLDivElement>(null);
  const sfx = useSfx(soundOn);

  const start = () => {
    if (running) return;
    const chosen = COLORS[randomInt(COLORS.length)]!;
    setRunning(true);
    sfx.whoosh();
    let n = 0;
    const step = () => {
      if (n++ > 12) {
        setColor(chosen);
        setRunning(false);
        setHistory((h) => [`${chosen.name} ${chosen.hex}`, ...h].slice(0, 50));
        sfx.fanfare();
        return;
      }
      setColor(COLORS[randomInt(COLORS.length)]!);
      sfx.tick();
      window.setTimeout(step, 55 + n * 10);
    };
    step();
  };

  return (
    <div className="grid gap-5 lg:grid-cols-[1fr_300px]">
      <div
        ref={stageRef}
        className="surface-card flex flex-col items-center justify-center gap-6 bg-card p-10"
      >
        <div
          className="size-48 rounded-3xl shadow-lift transition-colors duration-150"
          style={{ backgroundColor: color.hex }}
          aria-hidden
        />
        <p aria-live="polite" className="font-display text-3xl font-bold">
          {color.name} <span className="font-mono text-lg text-muted-foreground">{color.hex}</span>
        </p>
        <Button
          size="lg"
          className="h-14 px-12 text-lg font-semibold shadow-lift"
          disabled={running}
          onClick={start}
        >
          {running ? "Mixing…" : "Pick a colour"}
        </Button>
        <div className="no-print flex flex-wrap justify-center gap-2">
          <SoundToggle on={soundOn} onToggle={() => setSoundOn(!soundOn)} />
          <FullscreenButton targetRef={stageRef} />
          <CopyButton text={color.hex} label="Copy hex" />
          <ResetButton onClick={() => setHistory([])} />
        </div>
      </div>
      <HistoryPanel items={history} onClear={() => setHistory([])} title="Recent colours" />
    </div>
  );
}

/* ---------------- Random Line Order ---------------- */
export { LineOrder } from "./LineOrder";

/* ---------------- Classroom Scoreboard ---------------- */
export { Scoreboard } from "./Scoreboard";
